import { getOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import dayjs from 'dayjs';
import type { Hotelinfo } from 'frontend-utils/models';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { Day, Period } from 'lumen/models';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import {
  formatPercentage,
  getOccupancyRatio,
  hasOTBDefaultValue,
  OutOfOrderRooms,
} from 'lumen/utils/models/occupancy';
import isNumeric from 'lumen/utils/numeric/is-numeric';
import { getOtbUpdateString } from 'lumen/utils/otb-out-of-sync';

export interface OccupancyChange {
  benchmark_message: string;
  benchmark_value: number;
  extract_date: string;
  id: string;
  message: string;
  occupancy: string;
  rooms_booked: number;
  rooms_booked_group: number;
  rooms_booked_individual: number;
  rooms_out_of_order: number;
  total_capacity: number;
}

export default class Occupancy extends Model {
  [Type] = 'occupancy' as const;

  @belongsTo('day', { async: false, inverse: 'occupancies' })
  declare day: Day;

  @belongsTo('hotelinfo', { async: false, inverse: null })
  declare hotelinfo: Hotelinfo;

  @belongsTo('period', { async: false, inverse: 'occupancy' })
  declare period: Period;

  @attr('hoteldate')
  declare benchmarkExtractDate: NonNullable<HoteldateTransformReturn>;
  @attr('dayjs') declare benchmarkExtractDatetime: DayjsTransformReturn;
  @attr('string') declare benchmarkMessage: string;
  @attr('number') declare benchmarkValue: number;
  @attr('string') declare dateAggregation: string;
  @attr('hoteldate') declare extractDate: NonNullable<HoteldateTransformReturn>;
  // The extract date to which this extracted occupancy belongs to
  @attr('dayjs') declare extractDatetime: DayjsTransformReturn;
  @attr('dayjs') declare extractDateUtc: NonNullable<DayjsTransformReturn>;
  // The date and time when this extract was parsed
  @attr('string') declare message: string;
  @attr declare occupancyChanges?: OccupancyChange[];
  // possible origin values: 1 (occupancy), 2 (calculated), 3 (forcasted)
  @attr('number') declare origin: number;
  @attr('number') declare roomsBooked: number;
  @attr('number') declare roomsBookedGroup: number;
  @attr('number') declare roomsBookedIndividual: number;
  @attr('number') declare roomsOutOfOrder: number;
  @attr('number') declare totalCapacity: number;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  outOfOrderRooms = new OutOfOrderRooms(getOwner(this)!);

  get availableRooms(): number | null {
    const { isMissing, outOfOrderRooms, roomsOutOfOrder } = this;

    // If out of order rooms data is supported, roomsOutOfOrder can never be OTB_DEFAULT_VALUE, unless the data is missing.
    // If it is, set it to 0. This fixed an edge case.
    if (
      outOfOrderRooms.isSupported &&
      !isMissing &&
      hasOTBDefaultValue(roomsOutOfOrder)
    ) {
      this.roomsOutOfOrder = 0;
    }

    const { inventoryExcludingOOORooms, roomsBooked, totalCapacity } = this;

    // If out of order rooms data is supported, availableRooms is inventoryExcludingOOORooms - roomsBooked.
    // Otherwise, it's totalCapacity - roomsBooked.
    let capacityOrInventoryExclOOO: number | null = totalCapacity;

    if (outOfOrderRooms.isSupported) {
      capacityOrInventoryExclOOO = inventoryExcludingOOORooms;
    }

    if (!isNumeric(roomsBooked) || hasOTBDefaultValue(roomsBooked)) {
      return null;
    }

    if (
      !isNumeric(capacityOrInventoryExclOOO) ||
      hasOTBDefaultValue(capacityOrInventoryExclOOO)
    ) {
      return null;
    }

    return capacityOrInventoryExclOOO - roomsBooked;
  }

  get benchmarkValueAbsolute(): number | null {
    const { benchmarkValue, isBenchmarkMissing, totalCapacity } = this;

    if (isBenchmarkMissing || hasOTBDefaultValue(totalCapacity)) {
      return null;
    }

    return totalCapacity * benchmarkValue;
  }

  get benchmarkValueNormalized(): number | null {
    return this.isBenchmarkMissing ? null : this.benchmarkValue;
  }

  get formattedBenchmarkPercentage(): string {
    const formattedBenchmark = formatPercentage(
      this.intl,
      this.benchmarkValueNormalized,
    );
    return formattedBenchmark ?? this.intl.t('general.missing');
  }

  get formattedExtractDate(): string {
    return this.extractDate.format('YYYY-MM-DD');
  }

  get formattedOccupancyPercentage(): string {
    const formattedOccupancy = formatPercentage(
      this.intl,
      this.occupancyPercentage,
    );
    return formattedOccupancy ?? this.intl.t('general.missing');
  }

  get inventoryExcludingOOORooms(): number | null {
    const { roomsOutOfOrder, totalCapacity } = this;

    if (!isNumeric(totalCapacity) || hasOTBDefaultValue(totalCapacity)) {
      return null;
    }

    if (!isNumeric(roomsOutOfOrder) || hasOTBDefaultValue(roomsOutOfOrder)) {
      return null;
    }

    return totalCapacity - roomsOutOfOrder;
  }

  get inventoryExcludingOOORoomsPercentage(): number | null {
    const { inventoryExcludingOOORooms, totalCapacity } = this;

    if (!isNumeric(inventoryExcludingOOORooms)) {
      return null;
    }

    if (
      !isNumeric(totalCapacity) ||
      hasOTBDefaultValue(totalCapacity) ||
      totalCapacity === 0
    ) {
      return null;
    }

    return inventoryExcludingOOORooms / totalCapacity;
  }

  get isBenchmarkMissing(): boolean {
    return this.benchmarkMessage === 'general.missing';
  }

  get isCalculated(): boolean {
    return this.origin === 2;
  }

  // Indicates if market OTB is out of sync.
  // - Market OTB data can't be missing
  // - Linked day must not be in the past
  // - Benchmark extract date must be before the current hotel date.
  get isMarketOtbOutOfSync(): boolean {
    const { benchmarkExtractDate, isBenchmarkMissing, linkedDayIsPast } = this;

    if (!benchmarkExtractDate || isBenchmarkMissing || linkedDayIsPast) {
      return false;
    }

    const isBenchmarkExtractDateBeforeHotelDate = benchmarkExtractDate.isBefore(
      this.hotelAndMarketService.getLocalTime(),
      'day',
    );

    return isBenchmarkExtractDateBeforeHotelDate;
  }

  get isMissing(): boolean {
    return this.message === 'general.missing';
  }

  // Indicates if own OTB is out of sync.
  // - OTB data can't be missing
  // - Linked day must not be in the past
  // - Extract date must be before the current UTC date.
  get isOtbOutOfSync(): boolean {
    if (this.isMissing || this.linkedDayIsPast) {
      return false;
    }

    return this.extractDateUtc.isBefore(dayjs.utc(), 'day');
  }

  // Determines whether the day linked to the model is in the past.
  get linkedDayIsPast(): boolean {
    if (!this.day) {
      // Note: there's an edge case that doesn't support this check (=> MI when dealing with occupancy changes objects) - see occupancyDataForCurrentExtractDate
      // These objects are pushed in the store to make use of these model getters, but don't have a day value
      // Might be worth investigating later if we can handle this more cleanly
      // https://otainsight.slack.com/archives/GGLM7LJ4W/p1646909938964319
      return true;
    }

    return this.day.date.isBefore(
      this.hotelAndMarketService.getLocalTime(),
      'day',
    );
  }

  // Returns 'updated today' or 'updated ... days/months ago'.
  get marketOtbUpdateTimestamp(): string {
    return getOtbUpdateString(
      this.benchmarkExtractDate,
      this.hotelAndMarketService,
      this.intl,
    );
  }

  get occupancyGroupPercentage(): number | null {
    return getOccupancyRatio(this.roomsBookedGroup, this.totalCapacity);
  }

  get occupancyIndividualPercentage(): number | null {
    return getOccupancyRatio(this.roomsBookedIndividual, this.totalCapacity);
  }

  get occupancyPercentage(): number | null {
    return getOccupancyRatio(this.roomsBooked, this.totalCapacity);
  }

  // Returns 'updated today' or 'updated ... days/months ago'.
  get otbUpdateTimestamp(): string {
    return getOtbUpdateString(
      this.extractDate,
      this.hotelAndMarketService,
      this.intl,
    );
  }

  get outOfOrderRoomsPercentage(): number | null {
    const { roomsOutOfOrder, totalCapacity } = this;

    if (!isNumeric(roomsOutOfOrder) || hasOTBDefaultValue(roomsOutOfOrder)) {
      return null;
    }

    if (
      !isNumeric(totalCapacity) ||
      hasOTBDefaultValue(totalCapacity) ||
      totalCapacity === 0
    ) {
      return null;
    }

    return roomsOutOfOrder / totalCapacity;
  }

  get shouldShowRoomsOutOfOrder(): boolean {
    const { availableRooms, outOfOrderRooms, roomsOutOfOrder } = this;

    if (!outOfOrderRooms.isSupported) {
      return false;
    }

    if (
      !isNumeric(roomsOutOfOrder) ||
      roomsOutOfOrder === 0 ||
      hasOTBDefaultValue(roomsOutOfOrder)
    ) {
      return false;
    }

    if (!isNumeric(availableRooms) || hasOTBDefaultValue(availableRooms)) {
      return false;
    }

    return true;
  }

  /**
   * Calculates the difference between market benchmark of the current object and a change object in percentage points.
   *
   * @param  {Object} occupancyChange The change object to compare to.
   * @return {number} The difference in percentage points, null when at least one market benchmark value is missing.
   */
  benchmarkChangeVersusPercentage(
    occupancyChange: OccupancyChange | undefined,
  ): number | null {
    if (
      !occupancyChange ||
      occupancyChange.benchmark_message === 'general.missing'
    ) {
      return null;
    }

    if (this.isBenchmarkMissing) {
      return null;
    }

    return this.benchmarkValue - occupancyChange.benchmark_value;
  }

  /**
   * Helper function to get an occupancy change object for a certain extract date.
   *
   * @param  {String} extractDate The extract date as string in YYYY-MM-DD format for which to get the change object
   * @return {Object} The occupancy change object, or null if not found.
   */
  occupancyChangeForExtractDate(
    extractDate: string,
  ): OccupancyChange | undefined {
    return this.occupancyChanges?.find(
      (occupancyChange) => occupancyChange.extract_date === extractDate,
    );
  }

  /**
   * Calculates the difference between occupancy of the current object and a change object in percentage points.
   *
   * @param  {Object} occupancyChange The change object to compare to.
   * @return {number} The difference in percentage points, null when at least one occupancy value is missing.
   */
  occupancyChangeVersusPercentage(
    occupancyChange: OccupancyChange | undefined,
  ): number | null {
    if (!occupancyChange || occupancyChange.message === 'general.missing') {
      return null;
    }

    if (
      hasOTBDefaultValue(occupancyChange.rooms_booked) ||
      hasOTBDefaultValue(occupancyChange.total_capacity)
    ) {
      return null;
    }

    const { occupancyPercentage } = this;

    if (!isNumeric(occupancyPercentage)) {
      return null;
    }

    return (
      occupancyPercentage -
      occupancyChange.rooms_booked / occupancyChange.total_capacity
    );
  }

  /**
   * Calculates the difference between rooms booked of the current object and a change object in decimal numbers.
   *
   * @param  {Object} occupancyChange The change object to compare to.
   * @return {number} The difference in numbers, null when at least one occupancy value is missing.
   */
  roomsBookedChangeVersus(occupancyChange: OccupancyChange): number | null {
    if (!occupancyChange || occupancyChange.message === 'general.missing') {
      return null;
    }

    const { roomsBooked } = this;

    if (!isNumeric(roomsBooked)) {
      return null;
    }

    return roomsBooked - occupancyChange.rooms_booked;
  }
}
